import { Button, Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useEffect, useRef, useState } from 'react'
import { useMutation } from 'urql'
import type { GeolocationInput } from '../../gql/graphql'
import { ClockInMutation } from '../../graphql/timeclock'
import { submitQueueable } from '../../helpers/submitQueueable'
import { useTimeClockTasks } from '../../hooks/timeclock/useTimeClockTasks'
import { useNetwork } from '../../hooks/useNetwork'
import { useToast } from '../../hooks/useToast'
import { useGeoLocation } from './hooks/useGeolocation'

type ClockInModalProps = {
  onDismiss: () => void
}

const needsTaskSelection = (taskCount: number, selected?: number) =>
  taskCount > 0 && selected == null

const needsNote = (required: boolean, note: string) => required && !note.trim()

export const ClockInModal = ({ onDismiss }: ClockInModalProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const { tasks, loading: tasksLoading } = useTimeClockTasks()
  const [, clockIn] = useMutation(ClockInMutation)
  const { getCurrentCoords, preFetchGeolocation } = useGeoLocation({
    actionText: 'clock in',
  })

  const [selectedTask, setSelectedTask] = useState<number | undefined>()
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentTask = tasks.find((t) => t.id === selectedTask)
  const noteRequired = currentTask?.isNoteRequired ?? false
  const isGeoRequired = currentTask?.isGeoRequired ?? false

  useEffect(() => {
    if (!isGeoRequired) return
    preFetchGeolocation()
  }, [isGeoRequired, preFetchGeolocation])

  const taskSelectRef = useRef<HTMLIonSelectElement>(null)

  const handleSubmit = async () => {
    // Tasks not loaded yet ≠ org without tasks — no taskless punches
    if (tasksLoading) return
    if (needsTaskSelection(tasks.length, selectedTask)) {
      setError(_(msg`Please select a task`))
      taskSelectRef.current?.open()
      return
    }
    if (needsNote(noteRequired, note)) {
      setError(_(msg`A comment is required for this task`))
      return
    }

    setError(null)
    setSubmitting(true)

    try {
      let geolocation: GeolocationInput | undefined
      if (isGeoRequired) {
        const coords = await getCurrentCoords()
        if (!coords) return
        geolocation = coords
      }

      const exec = clockIn({
        data: {
          note: note || undefined,
          task: selectedTask != null ? { id: selectedTask } : undefined,
          geolocation,
        },
      })

      // Never block on the network — the optimistic update already reflects the
      // clock-in. Offline or a too-slow connection → queue and sync in the
      // background; a responsive connection still surfaces real server errors.
      const outcome = await submitQueueable(exec, { isOnline })
      if (outcome.status === 'error') {
        setError(outcome.result.error?.message ?? _(msg`An error occurred`))
        return
      }

      presentToast({
        color: 'success',
        message:
          outcome.status === 'queued'
            ? _(msg`Saved. It will sync when you're back online.`)
            : _(msg`You have been clocked in.`),
        duration: 3000,
      })
      onDismiss()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : _(msg`An error occurred`))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar>
          <IonTitle>{_(msg`Clock In`)}</IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={onDismiss}>{_(msg`Close`)}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonList>
          {tasks.length > 0 && (
            <IonItem>
              <IonLabel position='stacked'>{_(msg`Task`)}</IonLabel>
              <IonSelect
                ref={taskSelectRef}
                value={selectedTask}
                placeholder={_(msg`Select current task`)}
                interface='action-sheet'
                interfaceOptions={{
                  header: _(msg`Select a task`),
                }}
                onIonChange={(e) => setSelectedTask(e.detail.value)}
              >
                {tasks.map((task) => (
                  <IonSelectOption key={task.id} value={task.id}>
                    {task.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
          )}
          <IonItem>
            <IonLabel position='stacked'>
              {_(msg`Comments`)}
              {noteRequired ? ' (required)' : ''}
            </IonLabel>
            <IonTextarea
              value={note}
              onIonInput={(e) => setNote(e.detail.value ?? '')}
              placeholder={_(msg`Purpose of clock in`)}
              autoGrow
            />
          </IonItem>
        </IonList>

        {error && (
          <div className='ion-padding'>
            <Text variant='label' className='text-destructive'>
              {error}
            </Text>
          </div>
        )}

        <div className='ion-text-center ion-padding'>
          <Button onClick={handleSubmit} disabled={submitting || tasksLoading}>
            {submitting ? _(msg`Submitting...`) : _(msg`Clock In`)}
          </Button>
        </div>
      </IonContent>
    </>
  )
}
